import Link from "next/link";
import { forwardRef } from "react";

import { isOnline, removeInstLink } from "@/features/profile/utils";
import { Typography } from "@/shared/ui";
import MapPinIcon from "@/svgs/map-pin-alt.svg";
import PlaneIcon from "@/svgs/plane.svg";
import { calculateAge, getUserActivityText } from "@/utils";

import { Tag } from "../../../tag";

import styles from "./profile-info-full.module.scss";
import { ProfileInfoFullProps } from "./profile-info-full.types";

const ProfileInfoFull = forwardRef<HTMLDivElement, ProfileInfoFullProps>(
  (
    {
      firstName,
      dateOfBirth,
      lastSeen,
      socialLinks,
      occupation,
      locationWorkplace,
      locationResidence,
      visit,
      distanceKilometers,
      mutualUsers,
      isFreeTonight,
      closestTravel,
      distanceToClosestTravelKilometers,
    },
    ref
  ) => {
    return (
      <div ref={ref} className={styles.profileInfoFull}>
        <div className={styles.info}>
          <div className={styles.row}>
            <div className={styles.mainInfo}>
              <Typography bold variant="large-title" className={styles.name}>
                {firstName}
              </Typography>
              <Typography bold variant="footnote" className={styles.age}>
                {calculateAge(dateOfBirth)}
              </Typography>
              {isOnline(lastSeen) && <span className={styles.onlineDot} />}
            </div>

            {isFreeTonight && <Tag variant="green" label="Free tonight" />}
          </div>

          {socialLinks?.instagram && (
            <div className={styles.row}>
              <Link href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className={styles.instLink}>
                <Typography bold variant="footnote">
                  {removeInstLink(socialLinks.instagram)}
                </Typography>
              </Link>

              <Typography bold variant="footnote" className={styles.occupation}>
                {getUserActivityText({ occupation, locationWorkplace })}
              </Typography>
            </div>
          )}

          <div className={styles.row}>
            <div className={styles.distance}>
              <MapPinIcon />
              <Typography bold variant="footnote">
                {distanceToClosestTravelKilometers && closestTravel
                  ? `${distanceToClosestTravelKilometers}km from ${closestTravel?.location.label}`
                  : distanceKilometers <= 1
                  ? "less than km"
                  : `${distanceKilometers} km from you`}
              </Typography>
            </div>

            {!socialLinks?.instagram && (
              <Typography bold variant="footnote" className={styles.occupation}>
                {getUserActivityText({ occupation, locationWorkplace })}
              </Typography>
            )}
          </div>

          <div className={styles.row}>
            <div className={styles.locations}>
              {visit ? (
                <div className={styles.travelPlan}>
                  <Typography semi as="span" variant="footnote" className={styles.greyTypo}>
                    From
                  </Typography>
                  <Typography as="span" variant="footnote">
                    {locationResidence.label}
                  </Typography>

                  <PlaneIcon />

                  <Typography semi as="span" variant="footnote" className={styles.greyTypo}>
                    Visiting
                  </Typography>
                  <Typography semi as="span" variant="footnote">
                    {[visit.state ?? visit.city, visit.country].join(", ")}
                  </Typography>
                </div>
              ) : (
                <div className={styles.from}>
                  <Typography semi as="p" variant="footnote" className={styles.greyTypo}>
                    From
                  </Typography>
                  <Typography semi as="p" variant="footnote">
                    {locationResidence.label}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          <div className={styles.row}>
            {!!mutualUsers?.length && (
              <div className={styles.mutuals}>
                <Typography semi variant="footnote">
                  In common
                </Typography>
                <Typography semi variant="footnote">
                  {mutualUsers[0]?.firstName} {`${mutualUsers[0]?.lastName[0]}.`}{" "}
                  {mutualUsers.length > 1
                    ? `& ${mutualUsers.length - 1} other${mutualUsers.length === 1 ? "s" : ""}`
                    : ""}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ProfileInfoFull.displayName = "ProfileInfoFull";

export { ProfileInfoFull };
